import React, { useState } from 'react';
import StbModal, { StbModalProps, StbModalRole } from './StbModal';
import { alertsNegativeSmallBorder, alertsPositiveSmallBorder } from '@harbor/elements/icons';
import { HbrIcon } from '@harbor/elements-react-standalone';
import { MODAL_CLOSE_TIMER_MS } from './security-toolbox-ui-const';


type ResultType = 'success' | 'error';
interface Result {
  type: ResultType;
  message: string | null;
}

interface StbApiModalProps extends StbModalProps {
  showResultSection?: boolean;
  role?: StbModalRole;
  submitSuccess?: string;
  submitError?: string;
  closeTimer?: number;
  suppressResponses?: boolean;
  onSubmit: () => Promise<void>;
  onClose: () => Promise<void>;
}

const StbApiModal: React.FC<StbApiModalProps> = ({
  children,
  role = 'default',
  submitSuccess,
  submitError,
  closeTimer = MODAL_CLOSE_TIMER_MS,
  suppressResponses = false,
  onSubmit,
  onClose,
  ...rest
}) => {
  const [_result, setResult] = useState<Result | null>(null);
  const _successMessage = (role: StbModalRole) => {
    switch (role) {
      case 'delete':
        return 'Item deleted';
      case 'save':
        return 'Item saved';
      case 'update':
        return 'Item updated';
      default:
        return 'Operation successful';
    }
  }
  const _errorMessage = (role: StbModalRole) => {
    switch (role) {
      case 'delete':
        return 'Deletion failed';
      case 'save':
        return 'Save failed';
      case 'update':
        return 'Update failed';
      default:
        return 'Operation failed';
    }
  }

  const _message = _result
    ? _result.type === 'success'
      ? (submitSuccess && submitSuccess.length > 0
        ? submitSuccess
        : _result?.message ?? _successMessage(role))
      : (submitError && submitError.length > 0
        ? submitError
        : _result?.message ?? _errorMessage(role))
    : null;

  const _onSubmit = async () => {
    try {
      await onSubmit();
      if (role !== 'information' && !suppressResponses) {
        setResult({ type: 'success', message: null });
        await new Promise(resolve => {
          setTimeout(() => {
            setResult(null);
            resolve(null);
          }, closeTimer);
        });
      }
    } catch (e) {
      setResult({ type: 'error', message: (e as Error).message });
      throw e;
    }
  };

  const _onClose = async () => {
    setResult(null);
    await onClose();
  };

  return (
    <StbModal
      {...rest}
      role={role}
      onSubmit={_onSubmit}
      onClose={_onClose}
      apiResponseSection={
        _result
          ? (
              <div className={`stb-modal--response ${_result?.type ?? ''}`}>
                { _result.type === 'error'
                  ? <HbrIcon asset={alertsNegativeSmallBorder} size="20px" sentiment="danger" />
                  : <HbrIcon asset={alertsPositiveSmallBorder} size="20px" sentiment="success" />
                }
                <div className="ml-2">{_message}</div>
              </div>
            )
          : undefined
      }
    >
      {children}
    </StbModal>
  );
};

export default StbApiModal;